import React from 'react';
import { useRouter } from 'next/router';
import { useI18n } from 'next-localization';

import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';

import { useAddressLookup } from '@/hooks/use-address-lookup';
import { AddressRequest } from '@/types/SearchAddressData';
import { NeuButton } from '@hcahealthcaredpx/neutron-react';
import { useMyLocation, useStoredLocation } from '@xsf/shared-lib/hooks/location';

import { TypeaheadNew } from '../typeahead/typeahead-new';
import {
  TypeaheadItem,
  TypeaheadSelectable,
  ValidationRule,
} from '../typeahead/typeahead-new.types';
import {
  ensureZipCode,
  processTypeaheadData,
  userLocationToTypeaheadItem,
} from './location-input.utils';

type LocationInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  containerClassName?: string;
  // autofill from stored location on mount if no default value is provided
  autofillStoredLocation?: boolean;
  // auto select first item on blur if none selected
  autoSelectOnBlur?: boolean;
  // autofill from query param, provide the query param you would like to auto fill on
  autofillQueryParam?: string;
  // passthrough state setter to track typeahead query value externally
  trackTypeaheadQuery?: (query: string) => void;
  // validate typeaheadQuery (not field)
  typeaheadValidationRules?: ValidationRule[];
  // placeholder
  placeholder?: string;
  // do things when an item is selected
  onSelectItem?: (item: TypeaheadItem | null) => void;
  azRequestParams?: Omit<AddressRequest, 'query'>;
};

export const LocationInput = React.forwardRef<HTMLInputElement, LocationInputProps>(
  (props, ref) => {
    const { t } = useI18n();

    const {
      containerClassName,
      autofillStoredLocation = false,
      defaultValue,
      trackTypeaheadQuery,
      azRequestParams,
      autoSelectOnBlur,
      autofillQueryParam,
      onSelectItem,
      typeaheadValidationRules,
      placeholder = t('Search.EnterCityOrZipcode'),
      ...fieldProps
    } = props;

    // allow the parent to update the selected item
    const updateSelectedItemRef = React.useRef<(item: TypeaheadItem | null) => Promise<void>>(() =>
      Promise.resolve()
    );

    const { setError, clearErrors } = useFormContext();

    const router = useRouter();
    const [initialSelectedItem, setInitialSelectedItem] = React.useState<TypeaheadItem | null>(
      null
    );

    const { onUseMyLocationClick, userLocation: umlLocation, error: umlError } = useMyLocation();

    const [userLocation] = useStoredLocation();
    const {
      trigger: triggerTypeaheadLookup,
      isMutating: isAddressLookupLoading,
      data,
    } = useAddressLookup();

    React.useEffect(() => {
      if (fieldProps.name) {
        if (umlError) setError(fieldProps.name, { message: umlError });
        else clearErrors(fieldProps.name);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [umlError]);

    React.useEffect(() => {
      const handleLocationUpdate = async () => {
        if (umlLocation) {
          const typeaheadItem = userLocationToTypeaheadItem(umlLocation);
          updateSelectedItemRef.current(typeaheadItem);
        }
      };

      handleLocationUpdate();
    }, [umlLocation]);

    // Handle router query param for zipcode
    React.useEffect(() => {
      const query = router.query;

      if (router.isReady && autofillQueryParam) {
        if (
          autofillQueryParam in query &&
          typeof query[autofillQueryParam] === 'string' &&
          query[autofillQueryParam].trim().length > 0
        ) {
          triggerTypeaheadLookup({
            query: query[autofillQueryParam],
            ...azRequestParams,
            entityType: ['PostalCodeArea', 'Municipality', 'CountrySubdivision', 'Neighbourhood'],
          })
            .then((response) => {
              const taData = processTypeaheadData(response) as TypeaheadSelectable[];

              if (taData.length > 0) {
                setInitialSelectedItem(taData[0]);
              }
            })
            // just dont do anything if there was an error with the lookup, the filter will apply
            .catch(() => {});
        }

        if (
          (!autoSelectOnBlur || (autofillQueryParam && query[autofillQueryParam] == null)) &&
          autofillStoredLocation &&
          !defaultValue
        ) {
          if (userLocation) {
            const typeaheadItem = userLocationToTypeaheadItem(userLocation);
            setInitialSelectedItem(typeaheadItem);
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady]);

    const typeaheadData: TypeaheadItem[] = React.useMemo(() => processTypeaheadData(data), [data]);

    return (
      <div className={clsx('d-flex flex-column neu-form kind-single', containerClassName)}>
        <div className="d-flex flex-row justify-content-between">
          <label className="neu-form--label" htmlFor="location-input">
            {fieldProps.label}
          </label>
          <NeuButton
            id={`class-filters-${fieldProps.name}-btn`}
            className="neu-form--toggle"
            kind="tertiary"
            color="blue"
            size="small"
            onClick={async () => {
              const umlResult = await onUseMyLocationClick();
              if (umlResult.forceAutofill) {
                if (umlLocation) {
                  const typeaheadItem = userLocationToTypeaheadItem(umlLocation);
                  updateSelectedItemRef.current(typeaheadItem);
                }
              }
            }}
            role="button"
            tabIndex={0}
          >
            {t('Locations.Search.Form.UseMyLocation')}
          </NeuButton>
        </div>
        <TypeaheadNew
          searchProps={{
            id: 'location-input',
            label: fieldProps.label,
            name: `${fieldProps.name}-typeahead`,
            labelHidden: true,
            placeholder,
          }}
          fieldProps={{ ...fieldProps, name: fieldProps.name as string, id: fieldProps.id }}
          typeaheadProps={{
            autoSelectOnBlur,
            initialSelectedItem,
            trackTypeaheadQuery,
            updateSelectedItemRef,
            typeaheadResults: typeaheadData || [],
            triggerTypeaheadLookup: async (query: string) => {
              triggerTypeaheadLookup({ query, ...azRequestParams });
            },
            typeaheadLoading: isAddressLookupLoading,
            onTypeaheadKeyPress: () => {},
            processTypeaheadSelection: async (item: TypeaheadItem) => {
              const itemWithZip = await ensureZipCode(item);
              if (onSelectItem) {
                onSelectItem(itemWithZip);
              }
              return itemWithZip || item;
            },
            typeaheadValidationRules,
          }}
          ref={ref}
        />
      </div>
    );
  }
);

LocationInput.displayName = 'LocationInput';
